import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Database,
  Filter,
  List,
  RotateCcw,
  Search,
  Settings2,
  TreePine,
} from 'lucide-react';
import dayjs from 'dayjs';

import { PageShell } from '../components/PageShell';
import { KnowledgeTreeViewer } from '../components/KnowledgeTreeViewer';
import { ColumnFilterDropdown, ActiveFilterBadges } from '../components/ColumnFilterDropdown';
import { buildProductTree, getTreeStats } from '../services/product-knowledge-tree';
import { usePriceIntelStore } from '../stores/price-intel-store';

// ---------------------------------------------------------------------------
// 列定义：用户可配置显示/隐藏
// ---------------------------------------------------------------------------
interface ColumnDef {
  key: string;
  label: string;
  group: string;
  defaultVisible: boolean;
  render: (row: DataRow) => React.ReactNode;
  align?: 'left' | 'right';
  /** 是否支持下拉筛选（字符串类型的列适合） */
  filterable?: boolean;
  /** 从 DataRow 中获取用于筛选的原始字符串值 */
  filterValue?: (row: DataRow) => string;
}

type DataRow = Record<string, unknown> & {
  _id: string;
  _source: 'product' | 'price';
};

const mutedText: React.CSSProperties = { color: 'var(--color-system-secondary)' };
const mutedTertiary: React.CSSProperties = { color: 'var(--color-system-tertiary)' };

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-background-primary)',
  borderColor: 'var(--color-system-quaternary)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-sm)',
};

const strVal = (r: DataRow, k: string): string => { const v = r[k]; return typeof v === 'string' ? v : v != null ? String(v) : ''; };

const allColumns: ColumnDef[] = [
  // 基本信息
  { key: 'competitor', label: '竞品', group: '基本', defaultVisible: true, filterable: true, filterValue: (r) => strVal(r, 'competitor'), render: (r) => <span className="font-semibold">{r.competitor as string || '—'}</span> },
  { key: 'series', label: '系列', group: '基本', defaultVisible: true, filterable: true, filterValue: (r) => strVal(r, 'series'), render: (r) => <>{r.series || '—'}</> },
  { key: 'model', label: '型号', group: '基本', defaultVisible: true, render: (r) => <span className="font-medium">{r.model as string || '—'}</span> },
  { key: 'pitch_mm', label: 'Pitch(mm)', group: '基本', defaultVisible: true, align: 'right', filterable: true, filterValue: (r) => r.pitch_mm != null ? String(r.pitch_mm) : '', render: (r) => <span className="font-mono">{r.pitch_mm != null ? `${r.pitch_mm}` : '—'}</span> },
  // 规格
  { key: 'led_type', label: 'LED 类型', group: '规格', defaultVisible: true, filterable: true, filterValue: (r) => strVal(r, 'led_type'), render: (r) => <>{r.led_type || '—'}</> },
  { key: 'brightness', label: '亮度(nit)', group: '规格', defaultVisible: false, align: 'right', render: (r) => <span className="font-mono">{r.brightness ?? '—'}</span> },
  { key: 'refresh_rate', label: '刷新率(Hz)', group: '规格', defaultVisible: false, align: 'right', render: (r) => <span className="font-mono">{r.refresh_rate ?? '—'}</span> },
  { key: 'ip_rating', label: 'IP 等级', group: '规格', defaultVisible: false, filterable: true, filterValue: (r) => strVal(r, 'ip_rating'), render: (r) => <>{r.ip_rating || '—'}</> },
  { key: 'panel_size', label: '箱体尺寸', group: '规格', defaultVisible: false, render: (r) => {
    const w = r.panel_width_mm as number | null;
    const h = r.panel_height_mm as number | null;
    return <>{w && h ? `${w}×${h}mm` : '—'}</>;
  }},
  { key: 'weight_kg', label: '重量(kg)', group: '规格', defaultVisible: false, align: 'right', render: (r) => <span className="font-mono">{r.weight_kg ?? '—'}</span> },
  { key: 'max_power_w', label: '最大功耗(W)', group: '规格', defaultVisible: false, align: 'right', render: (r) => <span className="font-mono">{r.max_power_w ?? '—'}</span> },
  { key: 'indoor_outdoor', label: '环境', group: '规格', defaultVisible: true, filterable: true, filterValue: (r) => strVal(r, 'indoor_outdoor'), render: (r) => <>{r.indoor_outdoor || '—'}</> },
  // 价格
  { key: 'price_per_sqm', label: '每平米价', group: '价格', defaultVisible: true, align: 'right', render: (r) => {
    const v = r.price_per_sqm as number | null;
    return v != null ? <span className="font-mono font-semibold" style={{ color: 'var(--color-key)' }}>{r.currency} {v.toLocaleString()}</span> : <span style={mutedTertiary}>—</span>;
  }},
  { key: 'price_per_piece', label: '每片价', group: '价格', defaultVisible: false, align: 'right', render: (r) => {
    const v = r.price_per_piece as number | null;
    return v != null ? <span className="font-mono">{r.currency} {v.toLocaleString()}</span> : <span style={mutedTertiary}>—</span>;
  }},
  { key: 'year_quarter', label: '季度', group: '价格', defaultVisible: true, filterable: true, filterValue: (r) => strVal(r, 'year_quarter'), render: (r) => <>{r.year_quarter || '—'}</> },
  { key: 'region', label: '区域', group: '价格', defaultVisible: false, filterable: true, filterValue: (r) => [r.region, r.country].filter(Boolean).join(' · '), render: (r) => <>{[r.region, r.country].filter(Boolean).join(' · ') || '—'}</> },
  // 元信息
  { key: 'source', label: '来源', group: '元', defaultVisible: false, filterable: true, filterValue: (r) => r._source === 'product' ? '规格' : '价格', render: (r) => (
    <span className="rounded-full px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: r._source === 'product' ? 'rgba(16,185,129,0.1)' : 'rgba(0,113,227,0.1)', color: r._source === 'product' ? '#059669' : 'var(--color-key)' }}>
      {r._source === 'product' ? '规格' : '价格'}
    </span>
  )},
  { key: 'created_at', label: '录入时间', group: '元', defaultVisible: false, align: 'right', render: (r) => <span style={mutedTertiary}>{r.created_at ? dayjs(r.created_at as string).format('MM-DD HH:mm') : '—'}</span> },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
type ViewMode = 'table' | 'tree';

export function IntelDatabasePage() {
  const { facts, fetchFacts, productSpecs } = usePriceIntelStore();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultVisible).map((c) => c.key)),
  );
  const [showColPicker, setShowColPicker] = useState(false);
  // Excel 式列筛选：key = 列 key，value = 选中的值集合
  const [columnFilters, setColumnFilters] = useState<Map<string, Set<string>>>(new Map());

  useEffect(() => { void fetchFacts(); }, [fetchFacts]);

  // 合并数据源
  const allRows: DataRow[] = useMemo(() => {
    const fromSpecs = productSpecs.map((s) => ({ ...s, _id: s.temp_id, _source: 'product' as const }));
    const fromFacts = facts.map((f) => ({ ...f, _id: f.id, _source: 'price' as const }));
    return [...fromSpecs, ...fromFacts] as DataRow[];
  }, [productSpecs, facts]);

  // 每列的唯一值（用于下拉筛选）
  const uniqueValuesMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const col of allColumns) {
      if (!col.filterable || !col.filterValue) continue;
      const set = new Set<string>();
      for (const row of allRows) {
        const v = col.filterValue(row);
        if (v) set.add(v);
      }
      map.set(col.key, Array.from(set).sort());
    }
    return map;
  }, [allRows]);

  // 搜索 + 列筛选
  const filtered = useMemo(() => {
    let result = allRows;
    // 全局搜索
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((row) =>
        ['competitor', 'series', 'model', 'led_type', 'indoor_outdoor', 'region', 'country']
          .some((key) => {
            const v = row[key];
            return typeof v === 'string' && v.toLowerCase().includes(q);
          }),
      );
    }
    // 列筛选（AND 逻辑）
    for (const [colKey, selected] of columnFilters) {
      if (selected.size === 0) continue; // 空 = 不筛选
      if (selected.has('__NONE__')) { result = []; break; } // 清除所有
      const col = allColumns.find((c) => c.key === colKey);
      if (!col?.filterValue) continue;
      result = result.filter((row) => selected.has(col.filterValue!(row)));
    }
    return result;
  }, [allRows, searchQuery, columnFilters]);

  const handleColumnFilterChange = useCallback((colKey: string, selected: Set<string>) => {
    setColumnFilters((prev) => {
      const next = new Map(prev);
      if (selected.size === 0) next.delete(colKey);
      else next.set(colKey, selected);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => setColumnFilters(new Map()), []);
  const activeFilterCount = Array.from(columnFilters.values()).filter((v) => v.size > 0).length;

  const activeCols = allColumns.filter((c) => visibleCols.has(c.key));

  // 知识树
  const knowledgeTree = useMemo(() => buildProductTree(productSpecs), [productSpecs]);
  const stats = useMemo(() => getTreeStats(knowledgeTree), [knowledgeTree]);

  const toggleCol = (key: string) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <PageShell maxWidth="7xl">
      <div className="space-y-4">
        {/* 头部 */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--color-system-primary)' }}>
              <Database size={20} style={{ color: 'var(--color-key)' }} />
              数据库
            </h1>
            <p className="mt-1 text-sm" style={mutedText}>
              所有已确认的竞品情报数据。产品规格 + 价格信息统一管理。
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* 统计 */}
            <div className="flex gap-1.5">
              {[
                { label: '竞品', value: stats.totalCompetitors },
                { label: '产品', value: stats.totalProducts },
                { label: '价格', value: facts.length },
              ].map((s) => (
                <div key={s.label} className="rounded-lg px-2 py-1 text-center" style={{ backgroundColor: 'rgba(0,113,227,0.06)' }}>
                  <div className="text-xs font-bold" style={{ color: 'var(--color-key)' }}>{s.value}</div>
                  <div className="text-[9px]" style={mutedTertiary}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* 视图切换 */}
            <div className="flex rounded-lg border p-0.5" style={{ borderColor: 'var(--color-system-quaternary)' }}>
              {([
                { mode: 'table' as const, icon: List, label: '表格' },
                { mode: 'tree' as const, icon: TreePine, label: '层级' },
              ]).map(({ mode, icon: I, label }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition"
                  style={{ backgroundColor: viewMode === mode ? 'var(--color-key)' : 'transparent', color: viewMode === mode ? '#fff' : 'var(--color-system-secondary)' }}
                >
                  <I size={12} />{label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 树形视图 */}
        {viewMode === 'tree' && <KnowledgeTreeViewer tree={knowledgeTree} />}

        {/* 表格视图 */}
        {viewMode === 'table' && (
          <>
            {/* 搜索 + 列选择器 */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={mutedTertiary} />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索竞品、型号、系列..."
                  className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--color-key)]"
                  style={{ borderColor: 'var(--color-system-quaternary)', backgroundColor: 'var(--color-background-primary)' }}
                />
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowColPicker(!showColPicker)}
                  className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium transition hover:opacity-80"
                  style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
                >
                  <Settings2 size={13} />
                  列 ({activeCols.length}/{allColumns.length})
                </button>
                {showColPicker && (
                  <div
                    className="absolute right-0 top-full z-20 mt-1 w-56 rounded-xl border p-3 shadow-lg"
                    style={{ ...cardStyle }}
                  >
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest" style={mutedTertiary}>
                      显示列
                    </div>
                    {['基本', '规格', '价格', '元'].map((group) => (
                      <div key={group} className="mb-2">
                        <div className="mb-1 text-[10px] font-medium" style={mutedText}>{group}</div>
                        {allColumns.filter((c) => c.group === group).map((col) => (
                          <label key={col.key} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs transition hover:bg-[var(--color-background-secondary)]">
                            <input
                              type="checkbox"
                              checked={visibleCols.has(col.key)}
                              onChange={() => toggleCol(col.key)}
                              className="rounded"
                            />
                            <span style={{ color: 'var(--color-system-primary)' }}>{col.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setVisibleCols(new Set(allColumns.filter((c) => c.defaultVisible).map((c) => c.key)))}
                      className="mt-1 flex w-full items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-medium transition hover:opacity-80"
                      style={{ borderColor: 'var(--color-system-quaternary)', color: 'var(--color-system-secondary)' }}
                    >
                      <RotateCcw size={10} />
                      恢复默认
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 表格 */}
            <div className="overflow-hidden" style={cardStyle}>
              <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: 'var(--color-system-quaternary)' }}>
                <div className="text-sm font-semibold">
                  数据 <span style={mutedText}>共 {filtered.length} 条</span>
                </div>
                {(searchQuery || activeFilterCount > 0) && (
                  <div className="flex items-center gap-1.5 text-xs" style={mutedText}>
                    <Filter size={12} />已筛选{activeFilterCount > 0 ? `（${activeFilterCount} 列）` : ''}
                  </div>
                )}
              </div>

              {/* 活跃筛选标签 */}
              <ActiveFilterBadges
                columnFilters={columnFilters}
                columns={allColumns}
                onClear={(k) => handleColumnFilterChange(k, new Set())}
                onClearAll={clearAllFilters}
              />

              {filtered.length === 0 ? (
                <div className="px-4 py-16 text-center text-sm" style={mutedText}>
                  {allRows.length === 0
                    ? '暂无数据。在「录入」页面添加竞品情报，或点击侧栏底部的「样例」注入演示数据。'
                    : '没有匹配的结果。试试清除筛选条件。'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead style={{ backgroundColor: 'var(--color-background-secondary)' }}>
                      <tr>
                        {activeCols.map((col) => (
                          <th key={col.key} className="p-0">
                            {col.filterable ? (
                              <ColumnFilterDropdown
                                columnKey={col.key}
                                label={col.label}
                                uniqueValues={uniqueValuesMap.get(col.key) || []}
                                selectedValues={columnFilters.get(col.key) || new Set()}
                                onFilterChange={handleColumnFilterChange}
                                align={col.align}
                              />
                            ) : (
                              <div
                                className={`px-4 py-3 text-xs font-medium uppercase tracking-[0.08em] ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                style={mutedText}
                              >
                                {col.label}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row) => (
                        <tr
                          key={row._id}
                          className="border-t transition hover:bg-[var(--color-background-secondary)]"
                          style={{ borderColor: 'var(--color-system-quaternary)' }}
                        >
                          {activeCols.map((col) => (
                            <td
                              key={col.key}
                              className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : ''}`}
                              style={mutedText}
                            >
                              {col.render(row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}
